import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import adminApi from "../../api/adminApi";
import { useNotesContext } from "./NotesContext";

interface Props {
  noteId: string | undefined;
  onCancel: () => void;
  onDeleted: (noteId: string) => void;
}

const deleteNote = withErrorHandling(adminApi.deleteNote);

const DeleteNoteDialog = ({ noteId, onCancel, onDeleted }: Props) => {
  const { entityType, entityId, objectType } = useNotesContext();
  const [deleting, setDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleClose = () => {
    onCancel();
    setErrorMessage("");
  };

  const handleDelete = async () => {
    if (!noteId) {
      return;
    }

    setDeleting(true);
    const [resp, error] = await deleteNote(entityType, entityId, noteId, objectType);
    if (error || !resp.result) {
      setErrorMessage("Failed to delete the note");
    } else {
      onDeleted(noteId);
    }
    setDeleting(false);
  };

  return (
    <SmallDialog open={Boolean(noteId)} onClose={handleClose}>
      <DialogTitle>Delete</DialogTitle>
      <DialogCloseButton onClick={handleClose} />
      <DialogContent sx={(theme) => ({ px: theme.spacing(4) })}>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={0.5} sx={{ alignItems: "center" }}>
            <Typography variant="h6">Are you sure you want to delete this note?</Typography>
            {errorMessage && (
              <Typography color="error" sx={{ mt: 2 }}>
                {errorMessage}
              </Typography>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={handleClose}>
        <Button variant="outlined" color="error" loading={deleting} onClick={handleDelete}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteNoteDialog;
